import React from 'react';
import { Button } from '../../design-system';
import { FaEdit } from 'react-icons/fa';
import { LAST_UPDATED_COLUMN } from '../shared-constants';
import {
  currencyFormatter,
  prettyPhone,
  formatCurrencyOnBlur,
  composeValidators,
  required,
  emailVal,
  phoneNumberVal,
  formatTime,
} from '../../helpers/utils';
import DeadlineCell from '../../components/DeadlineCell';
import DescriptionTooltip from '../../components/DescriptionTooltip';
import TableLink from '../../components/TableLink';
import ButtonCellColumn from '../../components/ButtonCellColumn';
import { isCompleted } from '../../globalConstants';

import { StatusCellProject } from '../../components/StatusCell';

export const STATUSES = [
  {
    label: 'Awarded',
    value: 'Awarded',
  },
  {
    label: 'Completed',
    value: 'Completed',
  },
  {
    label: 'Cultivating',
    value: 'Cultivating',
  },
  {
    label: 'Declined',
    value: 'Declined',
  },
  {
    label: 'Deferred',
    value: 'Deferred',
  },
  {
    label: 'Go/No-Go',
    value: 'Go/No-Go',
  },
  {
    label: 'Never Seek',
    value: 'Never Seek',
  },
  {
    label: 'Pending',
    value: 'Pending',
  },
  {
    label: 'Planned',
    value: 'Planned',
  },
  {
    label: 'Preliminary',
    value: 'Preliminary',
  },
  {
    label: 'LOI Pending',
    value: 'LOI Pending',
  },
  {
    label: 'Withdrawn',
    value: 'Withdrawn',
  },
  {
    label: 'Working',
    value: 'Working',
  },
  {
    label: 'Hot Mess',
    value: 'Hot Mess',
  },
];

export const PROJECT_COLUMNS = [
  {
    Header: 'Project Description',
    accessor: 'description',
    Cell: (props) => {
      return (
        <TableLink to={`/projects/edit/${props.row.original.id}`}>
          <DescriptionTooltip {...props} maxLength={50} />
        </TableLink>
      );
    },
  },
  {
    Header: 'Client',
    accessor: 'client_name',
    Cell: ({ cell: { value }, row: { original } }) => {
      return (
        <TableLink to={`/clients/edit/${original.client_id}`}>
          {value || null}
        </TableLink>
      );
    },
  },
  {
    Header: 'Funder(s)',
    accessor: 'funder_name',
    Cell: ({ cell: { value }, row: { original } }) => {
      return (
        <TableLink to={`/funders/edit/${original.funder_id}`}>
          {value}
        </TableLink>
      );
    },
  },
];

export const SUBMIT_DATE_COLUMN = {
  Header: 'Submit Date',
  accessor: 'date_submitted',
  Cell: ({ cell: { value } }) => value && formatTime(value),
};

export const AMOUNT_REQUESTED_COLUMN = {
  Header: 'Amount Requested',
  accessor: 'amount_requested',
  alignRight: true,
  Cell: ({ cell: { value } }) => value && currencyFormatter.format(value),
};

export const EXPECTED_RESPONSE_COLUMN = {
  Header: 'Expected Response',
  accessor: 'expected_response',
  Cell: ({ cell: { value } }) => value && formatTime(value),
};

export const RESPONSE_DATE_COLUMN = {
  Header: 'Response Date',
  accessor: 'response_date',
  Cell: ({ cell: { value } }) => value && formatTime(value),
};

export const AWARDED_AMOUNT_COLUMN = {
  Header: 'Amount Awarded',
  accessor: 'amount_awarded',
  Cell: ({ cell: { value } }) => value && currencyFormatter.format(value),
};
export const STATUS_COLUMN = {
  Header: 'Status',
  accessor: 'status',
  Cell: (props) => <StatusCellProject statuses={STATUSES} {...props} />,
};

export const PROJECT_SECONDARY_COLUMNS = [
  {
    Header: 'Deadline',
    accessor: 'deadline',
    Cell: (props) => (
      <DeadlineCell typeOverride="project" hasDaysLeftCol {...props} />
    ),
  },
  { ...AMOUNT_REQUESTED_COLUMN },
  { ...STATUS_COLUMN },
  { ...LAST_UPDATED_COLUMN },
];

export const PENDING_COLUMNS = [
  { ...SUBMIT_DATE_COLUMN },
  { ...AMOUNT_REQUESTED_COLUMN },
  { ...STATUS_COLUMN },
  { ...EXPECTED_RESPONSE_COLUMN },
];

export const COMPLETED_COLUMNS = [
  { ...SUBMIT_DATE_COLUMN },
  { ...RESPONSE_DATE_COLUMN },
  { ...AMOUNT_REQUESTED_COLUMN },
  { ...AWARDED_AMOUNT_COLUMN },
  { ...STATUS_COLUMN },
];

export const RIGHT_COLUMN = [
  {
    Header: 'Project Lead',
    accessor: 'lead_user_name',
  },
];

export const TEAM_INFO_COLUMNS = [
  {
    Header: 'Name',
    accessor: 'name',
  },
  {
    Header: 'Email',
    accessor: 'email',
  },
  {
    Header: 'Phone',
    accessor: 'phone',
    Cell: ({ cell: { value } }) => <span>{prettyPhone(value)}</span>,
  },
  {
    Header: 'Notes',
    accessor: 'notes',
    Cell: (props) => <DescriptionTooltip {...props} maxLength={255} />,
  },
  {
    Header: 'Edit',
    disableSortBy: true,
    Cell: ({
      row: {
        original: { id, toggleEditTeamModal },
      },
    }) => (
      <ButtonCellColumn
        onClick={() => toggleEditTeamModal(id)}
        leftIcon={FaEdit}
      />
    ),
  },
  {
    Header: 'Delete',
    accessor: 'handleDeleteMember',
    disableSortBy: true,
    Cell: ({
      row: {
        original: { id, handleDeleteMember },
      },
    }) => (
      <Button
        onClick={() => handleDeleteMember(id)}
        leftIcon="FaRegTrashAlt"
        variant="transparent"
        leftIconProps={{ size: 20 }}
      />
    ),
  },
];

export const TEAM_INFO_FIELDS = [
  { name: 'name', label: 'Name', validate: required },
  {
    name: 'email',
    label: 'Email',
    validate: composeValidators(required, emailVal),
  },
  { name: 'phone', label: 'Phone', validate: phoneNumberVal },
  { name: 'notes', label: 'Notes', multiline: true },
];

export const ASSOCIATED_PROJECTS_COLUMNS = [
  {
    Header: 'Deadline',
    accessor: 'deadline',
    Cell: ({ cell: { value } }) => value && formatTime(value),
  },
  {
    Header: 'Project',
    accessor: 'description',
    Cell: ({ cell: { value }, row: { original } }) => {
      return (
        <TableLink to={`/projects/edit/${original.id}`}>{value}</TableLink>
      );
    },
  },
  {
    Header: 'Status',
    accessor: 'status',
  },
  {
    Header: 'Requested Amount',
    accessor: 'amount_requested',
    Cell: ({ cell: { value } }) => value && currencyFormatter.format(value),
  },
  {
    Header: 'Awarded',
    accessor: 'amount_awarded',
    Cell: ({ cell: { value } }) => value && currencyFormatter.format(value),
  },
  {
    Header: 'Hrs',
    accessor: 'actual_hours',
  },
];

export const PROJECT_TYPES = [
  {
    label: 'Grant',
    value: 'Project Grant',
  },
  {
    label: 'Operating Funds',
    value: 'Operating Funds',
  },
  {
    label: 'Capacity Building',
    value: 'Capacity Building',
  },
  {
    label: 'Recognition',
    value: 'Recognition',
  },
  {
    label: 'Other',
    value: 'Other',
  },
];

export const DEADLINE_TYPES = [
  {
    label: 'Annual',
    value: 'yearly',
  },
  {
    label: 'Semi-Annual',
    value: 'semi-Annual',
  },
  {
    label: 'Tri-Annual',
    value: 'tri-Annual',
  },
  {
    label: 'Quarterly',
    value: 'quarterly',
  },
  {
    label: 'Monthly',
    value: 'monthly',
  },
  {
    label: 'Variable',
    value: 'variable',
  },
  {
    label: 'Soft',
    value: 'soft',
  },
  {
    label: 'Other',
    value: 'other',
  },
];

export const FUNDING_TYPES = [
  {
    label: 'Grant',
    value: 'Grant',
  },
  {
    label: 'Multi-Year Grant',
    value: 'Multi-Year Grant',
  },
  {
    label: 'Capital Grant',
    value: 'Capital Grant',
  },
  {
    label: 'Renewal',
    value: 'Renewal',
  },
  {
    label: 'Matching Challenge',
    value: 'Matching Challenge',
  },
  {
    label: 'Matching Grant',
    value: 'Matching Grant',
  },
  {
    label: 'Bridge Loan',
    value: 'Bridge Loan',
  },
  {
    label: 'In-Kind',
    value: 'In-Kind',
  },
  {
    label: 'No Funding',
    value: 'No Funding',
  },
  {
    label: 'Other',
    value: 'Other',
  },
];

export const PROJECT_FIELDS = {
  generalInfo: [
    {
      name: 'description',
      label: 'Title',
      multiline: true,
      validate: required,
      width: '100%',
    },
    {
      name: 'client.name',
      label: 'Client',
      component: 'select',
    },
    {
      name: 'funder.name',
      label: 'Funder',
      component: 'select',
    },
    {
      name: 'project_type',
      label: 'Project Type',
      component: 'select',
      options: PROJECT_TYPES,
    },
    {
      name: 'project_lead',
      label: 'Project Lead',
      component: 'select',
    },
    {
      name: 'deadline_type',
      label: 'Deadline Frequency',
      component: 'select',
      options: DEADLINE_TYPES,
    },
    {
      name: 'deadline',
      label: 'Deadline',
      type: 'date',
    },
    {
      name: 'date_submitted',
      label: 'Date Submitted',
      type: 'date',
    },
    {
      name: 'expected_response',
      label: 'Expected Response',
      type: 'date',
    },
    {
      name: 'estimated_hours',
      label: 'Estimated Hours',
      type: 'number',
    },
    {
      name: 'funding_type',
      label: 'Funding Type',
      component: 'select',
      options: FUNDING_TYPES,
    },
    {
      name: 'status',
      label: 'Status',
      component: 'select',
      options: STATUSES,
    },
    {
      name: 'funded_program',
      label: 'Program to be funded',
      component: 'select',
    },
  ],
  awardInfo: [
    {
      name: 'amount_requested',
      label: 'Amount Requested',
      transformOnBlur: formatCurrencyOnBlur,
    },
    {
      name: 'amount_awarded',
      label: 'Amount Awarded',
      transformOnBlur: formatCurrencyOnBlur,
    },
    {
      name: 'award_start',
      label: 'Award Start',
      type: 'date',
    },
    {
      name: 'award_end',
      label: 'Award End',
      type: 'date',
    },
    {
      name: 'award_id',
      label: 'Award ID',
    },
    {
      name: 'response_date',
      label: 'Response Date',
      type: 'date',
    },
    {
      name: 'actual_hours',
      label: 'Actual Hours',
      type: 'number',
    },
    {
      name: 'empty',
      label: '',
    },
  ],
};

export const filterByStatus = (data: any[], status: string) => {
  switch (status) {
    case 'Active':
      return data.filter((item) => !isCompleted(item.status));
    case 'Completed':
      return data.filter((item) => isCompleted(item.status));
    case 'Pending':
      return data.filter((item) => item.status === status);
    default:
      return data;
  }
};

export const STATUS_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'Awarded', label: 'Awarded' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Cultivating', label: 'Cultivating' },
  { value: 'Declined', label: 'Declined' },
  { value: 'Deferred', label: 'Deferred' },
  { value: 'Go/No-Go', label: 'Go/No-Go' },
  { value: 'Hot Mess', label: 'Hot Mess' },
  { value: 'LOI Pending', label: 'LOI Pending' },
  { value: 'Never Seek', label: 'Never Seek' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Planned', label: 'Planned' },
  { value: 'Preliminary', label: 'Preliminary' },
  { value: 'Withdrawn', label: 'Withdrawn' },
  { value: 'Working', label: 'Working' },
];

export const DATE_RANGE_OPTIONS = [{ value: 'all', label: 'All' }];
